import { z } from 'zod';
import { emailSchema, phoneSchema, softDeletableSchema, uuidSchema } from './common';

export const locationKindSchema = z.enum(['studio', 'offsite_partner', 'venue', 'online']);
export type LocationKind = z.infer<typeof locationKindSchema>;

export const programTypeSchema = z.enum([
  'recurring_class',
  'camp',
  'intensive',
  'workshop',
  'private_lesson',
  'semi_private',
  'trio',
  'drop_in',
  'adult_membership',
  'mobile_preschool',
  'rental',
  'event_choreography',
  'performance_group',
  'competition_team',
]);
export type ProgramType = z.infer<typeof programTypeSchema>;

export const deliveryModeSchema = z.enum(['in_person', 'online', 'hybrid', 'offsite']);
export type DeliveryMode = z.infer<typeof deliveryModeSchema>;

export const studioLocationAddressSchema = z.object({
  line1: z.string().trim().min(1).max(160),
  line2: z.string().trim().max(160).optional(),
  city: z.string().trim().min(1).max(100),
  region: z.string().trim().min(1).max(100),
  postalCode: z.string().trim().min(1).max(24),
  country: z.string().trim().length(2),
});
export type StudioLocationAddress = z.infer<typeof studioLocationAddressSchema>;

export const studioLocationInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: locationKindSchema.default('studio'),
  address: studioLocationAddressSchema.optional(),
  timezone: z.string().trim().min(1).max(120).default('America/New_York'),
  contact_name: z.string().trim().min(1).max(120).optional(),
  contact_email: emailSchema.optional(),
  contact_phone: phoneSchema.optional(),
  active: z.boolean().default(true),
  notes: z.string().trim().max(2000).optional(),
});
export type StudioLocationInput = z.input<typeof studioLocationInputSchema>;

export const studioLocationSchema = softDeletableSchema.and(
  z.object({
    name: z.string(),
    kind: locationKindSchema,
    address: z.unknown().nullable(),
    timezone: z.string(),
    contact_name: z.string().nullable(),
    contact_email: z.string().nullable(),
    contact_phone: z.string().nullable(),
    active: z.boolean(),
    notes: z.string().nullable(),
  }),
);
export type StudioLocation = z.infer<typeof studioLocationSchema>;

export const programOfferingInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    program_type: programTypeSchema,
    delivery_mode: deliveryModeSchema.default('in_person'),
    default_location_id: uuidSchema.optional(),
    age_min_months: z.number().int().nonnegative().optional(),
    age_max_months: z.number().int().nonnegative().optional(),
    default_capacity: z.number().int().positive().optional(),
    default_price_cents: z.number().int().nonnegative().optional(),
    requires_trial: z.boolean().default(false),
    requires_waiver: z.boolean().default(true),
    active: z.boolean().default(true),
    description: z.string().trim().max(5000).optional(),
    notes: z.string().trim().max(2000).optional(),
  })
  .refine(
    (program) =>
      program.age_min_months === undefined ||
      program.age_max_months === undefined ||
      program.age_max_months >= program.age_min_months,
    {
      message: 'age_max_months must be greater than or equal to age_min_months',
      path: ['age_max_months'],
    },
  );
export type ProgramOfferingInput = z.input<typeof programOfferingInputSchema>;

export const programOfferingSchema = softDeletableSchema.and(
  z.object({
    name: z.string(),
    program_type: programTypeSchema,
    delivery_mode: deliveryModeSchema,
    default_location_id: uuidSchema.nullable(),
    age_min_months: z.number().int().nullable(),
    age_max_months: z.number().int().nullable(),
    default_capacity: z.number().int().nullable(),
    default_price_cents: z.number().int().nullable(),
    requires_trial: z.boolean(),
    requires_waiver: z.boolean(),
    active: z.boolean(),
    description: z.string().nullable(),
    notes: z.string().nullable(),
  }),
);
export type ProgramOffering = z.infer<typeof programOfferingSchema>;
